import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Mail, Phone, Newspaper } from "lucide-react";
import axios from "axios";

export const DemoRequest = () => {
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      company: "",
      email: "",
      phone: "",
    };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    // Company validation
    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-]+$/;
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.company.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      Object.values(errors).every(error => error === "") &&
      !loading
    );
  };

  const clearForm = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      message: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Form Validation Error",
        description: "Please check the form for errors.",
        variant: "destructive",
      });
      return;
    }

    if (!executeRecaptcha) {
      toast({
        title: "reCAPTCHA Error",
        description: "reCAPTCHA not initialized",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const token = await executeRecaptcha('submit_demo_request');

      const response = await axios.post(
        "https://lab7software.com/api.dynamico/api/Contact/SendContactEmail",
        {
          token,
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          requirements: formData.message,
        }
      );

      if (response.data.isPassed) {
        toast({
          title: "Success",
          description: "Your demo request has been successfully submitted. We'll get back to you soon!",
        });
        clearForm();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to submit demo request. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side content */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ready to Unlock Operational Excellence with Dynamico?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join us for a personalized demonstration of our cutting-edge no-code
              platform, designed to streamline field workflow automation.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              What to expect during the demo?
            </h3>
            <p className="text-gray-600 mb-6">
              Lets set up some time to walk through our no-code field workflow
              automation platform. Our digital transformation experts are excited to
              help you!
            </p>

            <ul className="space-y-4 text-gray-600">
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                Understanding of your processes & challenges
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                Demonstration of Dynamico reference app
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                Q&A session about your challenges and concerns
              </li>
            </ul>
          </div>

          {/* Right side form */}
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    placeholder="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <Input
                    placeholder="Company Name"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className={`w-full ${errors.company ? 'border-red-500' : ''}`}
                  />
                  {errors.company && (
                    <p className="text-red-500 text-sm mt-1">{errors.company}</p>
                  )}
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={`w-full ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <Textarea
                    placeholder="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full"
                    rows={4}
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={!isFormValid() || loading}
                >
                  {loading ? "Sending..." : "Request Demo"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Contact Cards Section */}
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          <div className="bg-white p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-blue-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-50 p-4 rounded-full mb-4">
                <Mail className="w-8 h-8 text-[#0077bb]" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Contact</h4>
              <a href="mailto:info@dynamico.com" className="text-gray-600 hover:text-[#0077bb] transition-colors font-medium">
                info@dynamico.com
              </a>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-blue-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-50 p-4 rounded-full mb-4">
                <Phone className="w-8 h-8 text-[#0077bb]" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Client Support</h4>
              <a href="mailto:support@dynamico.com" className="text-gray-600 hover:text-[#0077bb] transition-colors font-medium">
                support@dynamico.com
              </a>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl border border-blue-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-blue-50 p-4 rounded-full mb-4">
                <Newspaper className="w-8 h-8 text-[#0077bb]" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Press</h4>
              <a href="mailto:info@dynamico.com" className="text-gray-600 hover:text-[#0077bb] transition-colors font-medium">
                info@dynamico.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
